// CV Creator — shared data helpers (data model is independent of the rendering template).
export const uid = (p = "id") => `${p}_${Math.random().toString(36).slice(2, 9)}`;

export const PAGE_PX = 1123; // A4 height at 96dpi (297mm)

export const DEFAULT_CV = () => ({
  personal: { full_name: "", title: "", phone: "", email: "", location: "", linkedin: "", portfolio: "", github: "", website: "", photo_url: "" },
  summary: "",
  experience: [],
  education: [],
  skill_groups: [],
  certifications: [],
  projects: [],
  achievements: [],
  custom_sections: [],
  section_order: ["summary", "experience", "education", "skills", "certifications", "projects", "achievements"],
  job_description: { text: "", images: [] },
  ai_analysis: null,
  template: "modern",
  layout_settings: { sp: 1.0, fs: 1.0 },
});

export const newExperience = () => ({ id: uid("exp"), company: "", location: "", job_title: "", employment_type: "", start_date: "", end_date: "", current: false, description: "", achievements: [], technologies: [] });
export const newEducation = () => ({ id: uid("edu"), institution: "", degree: "", field: "", start_date: "", end_date: "", grade: "", description: "" });
export const newSkillGroup = (label = "Technical Skills") => ({ id: uid("skg"), label, skills: [] });
export const newCertification = () => ({ id: uid("cert"), name: "", organization: "", date: "", credential_id: "", credential_url: "" });
export const newProject = () => ({ id: uid("prj"), name: "", description: "", technologies: [], role: "", achievements: [], url: "" });
export const newAchievement = () => ({ id: uid("ach"), title: "", description: "", date: "" });
export const newCustomSection = (title = "Custom Section") => ({ id: uid("cst"), title, items: [] });
export const newCustomItem = () => ({ id: uid("cit"), heading: "", subheading: "", date: "", description: "", bullets: [] });

// Ensure imported/parsed CV data has stable ids + all arrays (so editor keys/reorder work).
export const normalizeImported = (parsed = {}) => {
  const base = DEFAULT_CV();
  const withIds = (arr, prefix) => (Array.isArray(arr) ? arr : []).map((x) => ({ id: uid(prefix), ...x }));
  return {
    ...base,
    personal: { ...base.personal, ...(parsed.personal || {}) },
    summary: parsed.summary || "",
    experience: withIds(parsed.experience, "exp").map((e) => ({ ...newExperience(), ...e })),
    education: withIds(parsed.education, "edu").map((e) => ({ ...newEducation(), ...e })),
    skill_groups: withIds(parsed.skill_groups, "skg").map((g) => ({ ...newSkillGroup(g.label || "Skills"), ...g })),
    certifications: withIds(parsed.certifications, "cert").map((c) => ({ ...newCertification(), ...c })),
    projects: withIds(parsed.projects, "prj").map((p) => ({ ...newProject(), ...p })),
    achievements: withIds(parsed.achievements, "ach").map((a) => ({ ...newAchievement(), ...a })),
    custom_sections: [],
  };
};

export const ASSIST_ACTIONS = [
  { key: "improve", label: "Improve wording" },
  { key: "professional", label: "Make professional" },
  { key: "concise", label: "Make concise" },
  { key: "ats_friendly", label: "Make ATS-friendly" },
  { key: "achievement", label: "Achievement-oriented" },
  { key: "add_impact", label: "Add measurable impact" },
  { key: "fix_grammar", label: "Fix grammar" },
  { key: "rewrite", label: "Rewrite" },
];

// move an item in an array (returns new array)
export const move = (arr, from, to) => {
  const a = [...arr];
  if (to < 0 || to >= a.length) return a;
  const [it] = a.splice(from, 1);
  a.splice(to, 0, it);
  return a;
};
